import { Label } from "../../../../../../../../library/label";
import FontPicker from "../../../../../../../../library/font-picker";
import { getIcon } from "../../../../../../../../utils/get-icon";
import ToggleButtons from "../../../../../../../../library/toggle-buttons";
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  AccordionTitle,
  InnerAccordionHeader,
} from "../styled";
import { FieldWrapper, Input } from "./styled";
import ColorSelect from "../../../../../../../../library/color-select";
import { ChangeEvent } from "react";
import { useSettings } from "../../../../context";
import { ColorResult } from "react-color";

const FontSettings = () => {
  const { fonts, updateFontSettings, getSelectedItemSettings } = useSettings();

  const currentSettings = getSelectedItemSettings();

  const handleFontFamilyChange = (selected: any) => {
    const selectedFont = fonts?.find(
      (font: any) => font.family === selected.value
    );

    if (selectedFont) {
      updateFontSettings({ family: selectedFont.family });
    }
  };

  const handleColorChange = (color: ColorResult) => {
    const { r, g, b, a } = color.rgb;
    const rgbaString = `rgba(${r}, ${g}, ${b}, ${a})`;

    updateFontSettings({ color: rgbaString });
  };

  const handleFontSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const size = parseInt(e.target.value, 10);
    if (!isNaN(size)) {
      updateFontSettings({ size });
    }
  };

  const handleLineHeightChange = (e: ChangeEvent<HTMLInputElement>) => {
    const size = parseFloat(e.target.value);
    if (!isNaN(size)) {
      updateFontSettings({ lineHeight: size });
    }
  };

  const onResetFontColor = () => {
    updateFontSettings({ color: "rgba(255, 255, 255, 1)" });
  };

  const handleDecorationChange = (value: string) => {
    updateFontSettings({ decoration: value });
  };

  const handleVariantChange = (value: string) => {
    if (value === "bold") {
      updateFontSettings({ weight: "bold", variant: "normal" });
    } else {
      updateFontSettings({ variant: value, weight: "normal" });
    }
  };

  const renderItalicAvailable = () => {
    const selectedFont = fonts?.find(
      (font: any) => font.family === currentSettings.configuration.font.family
    );

    if (selectedFont) {
      const variants = selectedFont.variants;
      return !variants.some((variant: string) => variant?.includes("italic"));
    }

    return false;
  };

  const renderBoldAvailable = () => {
    const selectedFont = fonts?.find(
      (font: any) => font.family === currentSettings.configuration.font.family
    );

    if (selectedFont) {
      const variants = selectedFont.variants;
      return !variants.some((variant: string) => {
        const weight = parseInt(variant.replace(/\D/g, ""), 10);
        return weight >= 500;
      });
    }

    return false;
  };

  return (
    <AccordionItem key="4" eventKey="4">
      <AccordionHeader>
        <InnerAccordionHeader>
          <AccordionTitle>{getIcon("")} Font</AccordionTitle>
        </InnerAccordionHeader>
      </AccordionHeader>
      <AccordionBody>
        <FieldWrapper>
          <Label>Type</Label>
          <FontPicker
            fonts={fonts}
            selectedFont={currentSettings.configuration.font.family}
            onSelect={handleFontFamilyChange}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Størrelse</Label>
          <Input
            type="number"
            min={1}
            max={100}
            name="fontSize"
            onChange={handleFontSizeChange}
            value={currentSettings.configuration.font.size}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Linjehøyde</Label>
          <Input
            type="number"
            min={0.1}
            max={10}
            step="0.1"
            name="lineHeight"
            onChange={handleLineHeightChange}
            value={currentSettings.configuration.font.lineHeight}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Farge</Label>
          <ColorSelect
            color={currentSettings.configuration.font.color}
            onChange={handleColorChange}
            onReset={onResetFontColor}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Dekorasjon</Label>
          <ToggleButtons
            selected={currentSettings.configuration.font.decoration}
            className="toggle-buttons"
            options={[
              {
                title: getIcon("dash"),
                value: "normal",
              },
              {
                title: getIcon("underline"),
                value: "underline",
              },
              {
                title: getIcon("strikethrough"),
                value: "line-through",
              },
            ]}
            onSelect={handleDecorationChange}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Variant</Label>
          <ToggleButtons
            selected={currentSettings.configuration.font.variant}
            className="toggle-buttons"
            options={[
              {
                title: getIcon("regular-text"),
                value: "normal",
              },
              {
                title: getIcon("italic-text"),
                value: "italic",
                disabled: renderItalicAvailable(),
              },
              {
                title: getIcon("bold-text"),
                value: "bold",
                disabled: renderBoldAvailable(),
              },
            ]}
            onSelect={handleVariantChange}
          />
        </FieldWrapper>
      </AccordionBody>
    </AccordionItem>
  );
};

export default FontSettings;
